import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const iconButtonVariants = cva(
  "inline-flex items-center justify-center shrink-0 transition-all " +
  "outline-none active:scale-95 " +
  "disabled:pointer-events-none",
  {
    variants: {
      variant: {
        // same tokens as Button — only shape/size differs
        primary:
          "bg-black text-white " +
          "hover:bg-btn-primary-hover " +
          "focus:ring focus:ring-ring focus:ring-offset-0 " +
          "disabled:bg-epic-neutral-8 disabled:text-white",

        brand:
          "bg-brand text-white " +
          "hover:bg-btn-brand-hover " +
          "focus:ring focus:ring-brand focus:ring-offset-0 " +
          "disabled:bg-epic-neutral-8 disabled:text-white",

        // bg=general-secondary | hover=secondary-icon-hover | focus=general-secondary+ring | disabled=general-secondary/50 | text=white
        secondary:
          "bg-general-secondary text-white " +
          "hover:bg-secondary-icon-hover " +
          "focus:bg-general-secondary focus:ring focus:ring-ring focus:ring-offset-0 " +
          "disabled:bg-general-secondary disabled:opacity-50",

        // bg=btn-ghost-bg(rgba255,255,255,0.2) | border=foreground | text=foreground | radius=rounded-lg via compound
        outline:
          "border border-foreground bg-btn-ghost-bg text-foreground " +
          "hover:[background:var(--button-outline-bg-hover)] " +
          "focus:[background:var(--button-outline-bg-default)] focus:[box-shadow:0_0_0_3px_var(--focus-ring)] " +
          "disabled:border-[var(--button-outline-stroke-disabled)] disabled:[background:var(--button-outline-bg-disabled)] disabled:text-epic-neutral-11",

        ghost:
          "bg-btn-ghost-bg text-foreground " +
          "hover:[background:var(--button-ghost-bg-hover)] " +
          "focus:[background:var(--button-ghost-bg-default)] focus:[box-shadow:0_0_0_3px_var(--focus-ring)] " +
          "disabled:[background:var(--button-ghost-bg-disabled)] disabled:text-epic-neutral-8",

        destructive:
          "bg-destructive text-destructive-foreground " +
          "hover:bg-btn-destructive-hover " +
          "focus:ring focus:ring-destructive/20 focus:ring-offset-0 " +
          "disabled:bg-red-300 disabled:text-white",

        "ghost-muted":
          "bg-btn-ghost-bg text-slate-700 " +
          "hover:[background:var(--button-ghost-bg-default)] " +
          "focus:[background:var(--button-ghost-bg-default)] focus:[box-shadow:0_0_0_3px_var(--focus-ring)] " +
          "disabled:[background:var(--button-ghost-bg-default)] disabled:opacity-50",
      },
      size: {
        // width = height (square); matches Button size heights exactly
        large:   "h-10 w-10",  // 40×40px
        regular: "h-9  w-9",   // 36×36px
        small:   "h-8  w-8",   // 32×32px
        mini:    "h-6  w-6",   // 24×24px
      },
      rounded: {
        default: "rounded-sm",
        full:    "rounded-full",
      },
    },
    compoundVariants: [
      { variant: "secondary",   rounded: "default", class: "rounded-lg" },
      { variant: "outline",     rounded: "default", class: "rounded-lg" },
      { variant: "ghost",       rounded: "default", class: "rounded-lg" },
      { variant: "ghost-muted", rounded: "default", class: "rounded-lg" },
    ],
    defaultVariants: {
      variant: "primary",
      size:    "regular",
      rounded: "default",
    },
  }
)

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, rounded, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
IconButton.displayName = "IconButton"

export { IconButton, iconButtonVariants }
